import { JsonSchema } from '../models/transformers.model';
import { resolveAllRefs } from './json-schema-utils';
import { isNil } from './shared-utils';

/**
 * Extracts default values from a JSON schema. Traverses schema properties recursively and
 * collects all `default` values. Handles $ref resolution using the existing resolveAllRefs
 * utility.
 *
 * @param schema - The property schema to extract defaults from
 * @param rootSchema - The root schema for resolving $ref references
 * @returns An object containing default values for properties that have them
 */
export function extractDefaultsFromSchema(
  schema: JsonSchema,
  rootSchema?: JsonSchema
): Record<string, unknown> {
  const root = rootSchema ?? schema;
  const resolvedSchema = resolveAllRefs(schema, root);

  return extractDefaultsRecursive(resolvedSchema, root);
}

function extractDefaultsRecursive(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
  rootSchema: JsonSchema
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  if (isNil(schema) || typeof schema !== 'object') {
    return defaults;
  }

  // Handle the schema's own default
  if ('default' in schema && schema.default !== undefined) {
    return schema.default;
  }

  // Handle object properties
  if (schema.properties && typeof schema.properties === 'object') {
    for (const [key, propSchema] of Object.entries(schema.properties)) {
      const resolvedProp = resolveAllRefs(propSchema, rootSchema);
      const propDefault = extractPropertyDefault(resolvedProp, rootSchema);

      if (propDefault !== undefined) {
        defaults[key] = propDefault;
      }
    }
  }

  return defaults;
}

function extractPropertyDefault(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  propSchema: any,
  rootSchema: JsonSchema
): unknown {
  if (isNil(propSchema) || typeof propSchema !== 'object') {
    return undefined;
  }

  // Direct default value
  if ('default' in propSchema && propSchema.default !== undefined) {
    return propSchema.default;
  }

  // Handle nested object with properties
  if (propSchema.type === 'object' && propSchema.properties) {
    const nestedDefaults = extractDefaultsRecursive(propSchema, rootSchema);
    // Only return nested defaults if there are any
    if (Object.keys(nestedDefaults).length > 0) {
      return nestedDefaults;
    }
  }

  // Handle arrays with item defaults
  if (propSchema.type === 'array' && propSchema.items) {
    if ('default' in propSchema.items && propSchema.items.default !== undefined) {
      return [propSchema.items.default];
    }
  }

  return undefined;
}
